// Timezone-safe date helpers for the Front Desk. All operate on "YYYY-MM-DD" strings and never touch
// toISOString() (which converts to UTC and shifts the day in WIB). "Today" is the Asia/Jakarta (WIB)
// calendar day regardless of the browser's own timezone.
const DAYS = {
  id: ["Min", "Sen", "Sel", "Rab", "Kam", "Jum", "Sab"],
  en: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
};

const MONTHS = {
  id: [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "Mei",
    "Jun",
    "Jul",
    "Agu",
    "Sep",
    "Okt",
    "Nov",
    "Des",
  ],
  en: [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ],
};

// Intl.DateTimeFormat construction is one of the heavier browser calls (locale + timezone
// resolution), so build the WIB formatters once and reuse them — the formatters are stateless.
const WIB_DATE_FMT = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Asia/Jakarta",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

const WIB_TIME_FMT = new Intl.DateTimeFormat("en-GB", {
  timeZone: "Asia/Jakarta",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
});

// Shift a YYYY-MM-DD string by N days, staying on the local calendar (no UTC conversion).
export const shiftIso = (isoDate, days) => {
  const [year, month, day] = isoDate.split("-").map(Number);
  const dt = new Date(year, month - 1, day);
  dt.setDate(dt.getDate() + days);
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

// Today's date on the WIB (Asia/Jakarta) calendar, as YYYY-MM-DD.
export const wibTodayIso = () => WIB_DATE_FMT.format(new Date());

// "Kam, 02 Jul 2026" (id) / "Thu, 02 Jul 2026" (en) from a YYYY-MM-DD string.
// pad=false gives the non-padded day used in body copy ("Kam, 2 Jul 2026").
export const formatDate = (isoDate, lang, pad = true) => {
  if (!isoDate) {
    return "";
  }
  const [year, month, day] = isoDate.split("-").map(Number);
  const weekday = new Date(year, month - 1, day).getDay();
  const days = DAYS[lang] || DAYS.id;
  const months = MONTHS[lang] || MONTHS.id;
  const dayStr = pad ? String(day).padStart(2, "0") : String(day);
  return `${days[weekday]}, ${dayStr} ${months[month - 1]} ${year}`;
};

// "Kam, 2 Jul 2026, 12:22:05 WIB" from an ISO instant (24h, WIB, non-padded day).
export const formatDateTime = (isoInstant, lang) => {
  if (!isoInstant) {
    return "";
  }
  const instant = new Date(isoInstant);
  return `${formatDate(wibDateIso(instant), lang, false)}, ${wibTime(instant)} WIB`;
};

// WIB wall-clock time "HH:MM:SS" for a given Date instant.
export const wibTime = (instant) => WIB_TIME_FMT.format(instant);

// WIB date as YYYY-MM-DD for a given Date instant (for the ticking clock's date part).
export const wibDateIso = (instant) => WIB_DATE_FMT.format(instant);

// --- Transit hour labels ---------------------------------------------------------------------
//
// The front desk reads a 24-hour clock as if it were 12-hour and sells 09:00 to a guest arriving at
// 21:00 — a slot that already ended that morning. These helpers keep the 24-hour value (which is what
// the server stores and what every other reader parses) and add the 12-hour reading plus the
// Indonesian day-part beside it, so the evening is unmistakable at a glance.
//
// DISPLAY ONLY. Never feed the output of these back into a form value, a request body or a
// comparison — the bare "HH:MM" remains the wire format everywhere.
const DAY_PARTS = [
  { untilMinutes: 3 * 60, key: "dini_hari", icon: "🌙" },
  { untilMinutes: 11 * 60, key: "pagi", icon: "🌅" },
  { untilMinutes: 15 * 60, key: "siang", icon: "☀️" },
  { untilMinutes: 18 * 60, key: "sore", icon: "🌇" },
  { untilMinutes: 24 * 60, key: "malam", icon: "🌙" },
];

// "HH:MM" (or an ISO instant) -> { hours, minutes } on the WIB wall clock, or null when unparseable.
const wallClock = (value) => {
  if (!value) return null;

  const text = String(value);
  // An ISO instant carries its own +07:00 offset, so the wall clock is at a fixed position.
  const source =
    text.length > 5 && text.includes("T") ? text.slice(11, 16) : text;
  const match = source.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return null;

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isFinite(hours) || hours > 23 || !Number.isFinite(minutes))
    return null;

  return {
    hours,
    minutes,
    hhmm: `${String(hours).padStart(2, "0")}:${match[2]}`,
  };
};

// The day-part bucket for a time, e.g. dayPart("21:00") -> { key: "malam", icon: "🌙" }.
export const dayPart = (value) => {
  const clock = wallClock(value);
  if (!clock) return null;

  const minutes = clock.hours * 60 + clock.minutes;
  return DAY_PARTS.find((part) => minutes < part.untilMinutes) || null;
};

// 12-hour reading of a 24-hour time. Midnight is 12 AM and noon is 12 PM, which is the pair people
// get wrong when they write this by hand.
const twelveHour = (hours) => {
  const suffix = hours < 12 ? "AM" : "PM";
  const twelve = hours % 12 === 0 ? 12 : hours % 12;
  return `${twelve} ${suffix}`;
};

// "21:00 (9 PM / Malam) 🌙" — for the places where the hour is being CHOSEN or confirmed.
// No `lang` argument: `t` already resolves the day-part word, and AM/PM is language-neutral.
export const timeWithDayPart = (value, t) => {
  const clock = wallClock(value);
  if (!clock) return "";

  const part = dayPart(clock.hhmm);
  if (!part) return clock.hhmm;

  const word = t ? t(`daypart_${part.key}`) : "";
  const reading = word
    ? `${twelveHour(clock.hours)} / ${word}`
    : twelveHour(clock.hours);
  return `${clock.hhmm} (${reading}) ${part.icon}`;
};

// "21:00 🌙" — for dense surfaces (the grid cell, the recovery dock) where the full label would wrap.
export const timeCompact = (value) => {
  const clock = wallClock(value);
  if (!clock) return "";

  const part = dayPart(clock.hhmm);
  return part ? `${clock.hhmm} ${part.icon}` : clock.hhmm;
};
